import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Star } from "lucide-react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import Footer from "@/components/site/Footer";

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErr("");
    Promise.all([api.get(`/projects/${id}`), api.get("/projects")])
      .then(([{ data }, { data: all }]) => {
        if (!mounted) return;
        setProject(data);
        setRelated(all.filter((p) => p.id !== id).slice(0, 4));
        window.scrollTo({ top: 0, behavior: "instant" });
      })
      .catch((e) =>
        mounted &&
        setErr(formatApiError(e.response?.data?.detail) || e.message || "Not found")
      )
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id]);

  return (
    <main data-testid="project-detail" className="bg-ink text-bone min-h-screen grain">
      <div className="px-6 md:px-12 py-6 border-b border-ink-border flex items-center justify-between sticky top-0 bg-ink/90 backdrop-blur z-40">
        <Link
          to="/"
          data-testid="back-to-portfolio"
          className="flex items-center gap-2 font-manrope text-xs uppercase tracking-[0.3em] text-bone/80 hover:text-bone"
        >
          <ArrowLeft className="h-3 w-3" /> Back
        </Link>
        <span className="font-manrope text-xs uppercase tracking-[0.3em] text-bone/50">
          Project detail
        </span>
      </div>

      {loading && (
        <div className="px-6 md:px-12 py-32 font-manrope text-bone/60">Loading…</div>
      )}
      {err && !loading && (
        <div className="px-6 md:px-12 py-32 font-manrope text-red-400">{err}</div>
      )}

      {project && !loading && (
        <>
          {/* Hero */}
          <section className="relative w-full bg-ink-surface overflow-hidden border-b border-ink-border">
            <motion.div
              initial={{ scale: 1.05, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="aspect-[16/9] md:aspect-[21/9] w-full"
            >
              <img
                src={mediaUrl(project.cover_image)}
                alt={project.title}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
            </motion.div>
          </section>

          {/* Body */}
          <section className="px-6 md:px-12 py-20 md:py-28">
            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
              <div className="md:col-span-8">
                <div className="flex items-center gap-3 mb-6 font-manrope text-[10px] uppercase tracking-[0.3em] text-bone/60 flex-wrap">
                  <span>{project.category}</span>
                  {project.year ? (
                    <>
                      <span className="h-px w-6 bg-bone/30" />
                      <span>{project.year}</span>
                    </>
                  ) : null}
                  {project.client ? (
                    <>
                      <span className="h-px w-6 bg-bone/30" />
                      <span>{project.client}</span>
                    </>
                  ) : null}
                  {project.featured && (
                    <>
                      <span className="h-px w-6 bg-bone/30" />
                      <span className="inline-flex items-center gap-1 text-bone">
                        <Star className="h-3 w-3 fill-bone" /> Featured
                      </span>
                    </>
                  )}
                </div>

                <h1
                  data-testid="project-title"
                  className="font-oswald uppercase tracking-tighter leading-[0.9] text-bone text-5xl md:text-7xl lg:text-8xl"
                >
                  {project.title}
                </h1>

                {project.description ? (
                  <p className="mt-8 font-cormorant italic text-2xl md:text-3xl text-bone/85 max-w-2xl leading-snug">
                    {project.description}
                  </p>
                ) : null}

                {project.long_description ? (
                  <p className="mt-8 font-manrope text-base md:text-lg text-bone/75 leading-relaxed max-w-2xl whitespace-pre-line">
                    {project.long_description}
                  </p>
                ) : null}

                {project.behance_url ? (
                  <a
                    href={project.behance_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="open-on-behance"
                    className="mt-10 inline-flex items-center gap-3 bg-bone text-ink font-oswald uppercase tracking-tight text-lg px-6 py-3 hover:bg-bone/90 transition-colors"
                  >
                    View on Behance <ExternalLink className="h-4 w-4" />
                  </a>
                ) : null}
              </div>

              <aside className="md:col-span-4 md:border-l md:border-ink-border md:pl-10">
                {project.client ? <Meta label="Client" value={project.client} /> : null}
                {project.role ? <Meta label="Role" value={project.role} /> : null}
                <Meta label="Category" value={project.category} />
                <Meta label="Year" value={project.year || "—"} />
                <Meta label="Director" value="Samuel Ortiz" />
              </aside>
            </div>
          </section>

          {/* Gallery — Behance-style vertical stack */}
          {Array.isArray(project.gallery) && project.gallery.length > 0 && (
            <section
              data-testid="gallery-section"
              className="border-t border-ink-border bg-ink"
            >
              <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-20 space-y-6 md:space-y-10">
                {project.gallery.map((g, i) => (
                  <GalleryItem key={i} item={g} index={i} />
                ))}
              </div>
            </section>
          )}

          {/* Related */}
          {related.length > 0 && (
            <section className="px-6 md:px-12 py-20 border-t border-ink-border">
              <div className="max-w-6xl mx-auto">
                <div className="flex items-baseline gap-4 mb-10">
                  <span className="font-manrope text-xs uppercase tracking-[0.3em] text-bone/60">
                    More projects
                  </span>
                  <span className="h-px flex-1 bg-bone/15" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/proyecto/${r.id}`}
                      data-testid={`related-${r.id}`}
                      className="group"
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-ink-surface">
                        <img
                          src={mediaUrl(r.cover_image)}
                          alt={r.title}
                          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                      <h3 className="mt-3 font-oswald uppercase tracking-tight text-base text-bone">
                        {r.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}
      <Footer />
    </main>
  );
}

function GalleryItem({ item, index }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="w-full"
      data-testid={`gallery-item-${index}`}
    >
      <img
        src={mediaUrl(item.url)}
        alt={item.caption || `Piece ${index + 1}`}
        loading="lazy"
        className="block w-full h-auto bg-ink-surface"
      />
      {item.caption ? (
        <figcaption className="mt-3 font-manrope text-xs text-bone/50 tracking-wide">
          {item.caption}
        </figcaption>
      ) : null}
    </motion.figure>
  );
}

function Meta({ label, value }) {
  return (
    <div className="mb-6">
      <div className="font-manrope text-[10px] uppercase tracking-[0.3em] text-bone/50 mb-1">
        {label}
      </div>
      <div className="font-oswald uppercase tracking-tight text-lg text-bone">{value}</div>
    </div>
  );
}
